import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import { Button } from '@components/atoms/button';
import { CameraAccessDefault } from '@components/atoms/camera-access-default';
import { CornerLine } from '@components/atoms/corner-line-window';
import { Icon } from '@components/atoms/icon';
import { Spinner } from '@components/atoms/spinner';
import { Typography } from '@components/atoms/typography';
import { CloseIcon } from '@components/icons';
import { CameraVariantLineEnum, DocsVerificationInternalStepEnum } from '@models/enums';
import type { FileWithPreview } from '@models/types';
import type { TakeDocumentProps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/downloads/take-document/types';
import { base64ToFile } from '@utils/util';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { TakePhotoContainer } from './styles';

const videoConstraints = {
    facingMode: 'user',
    width: {
        ideal: 1920,
        min: 640,
    },
    height: {
        ideal: 1080,
        min: 480,
    },
};

const constraints = {
    audio: false,
    video: videoConstraints,
};

export const TakeDocument: React.FC<TakeDocumentProps> = ({
    title,
    onChangeFile,
    onClose,
    className,
    variant,
}) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState<DocsVerificationInternalStepEnum>(
        DocsVerificationInternalStepEnum.CAMERA_ACCESS,
    );
    const [cameraNotAllowed, setIsCameraNotAllowed] = useState(false);
    const [previewFile, setPreviewFile] = useState<FileWithPreview | null>(null);
    const webcamRef = useRef<Webcam>(null);

    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log('Start getUserMedia');
        navigator.mediaDevices
            .getUserMedia(constraints)
            .then((stream) => {
                const tracks = stream.getTracks();

                tracks?.forEach((track) => {
                    track.stop();
                });
                // eslint-disable-next-line no-console
                console.log('Success getUserMedia', stream);
                setCurrentStep(DocsVerificationInternalStepEnum.SCANNING);
            })
            .catch((e) => {
                // eslint-disable-next-line no-console
                console.log('Error getUserMedia', e);
                setIsCameraNotAllowed(true);
            });
    }, []);

    const capture = useCallback(async () => {
        if (webcamRef.current) {
            const imageSrc = webcamRef.current.getScreenshot();

            const file = (await base64ToFile(
                imageSrc as string,
                `photo_${uuidv4()}.jpeg`,
            )) as FileWithPreview;

            const doneFile = Object.assign(file, {
                preview: imageSrc,
            });

            setPreviewFile(doneFile);
        }
    }, []);

    const handleClose = () => {
        onClose?.();
    };

    const handleFileChange = () => {
        if (previewFile) {
            onChangeFile(previewFile);
        }
        if (onClose) {
            onClose(true);
        }
    };

    const handleRedoClick = () => {
        setPreviewFile(null);
    };

    const handleUserMediaError = useCallback((error: string | DOMException) => {
        // eslint-disable-next-line no-console
        console.log('Camera error', error);
        setIsCameraNotAllowed(true);
    }, []);

    return (
        <TakePhotoContainer
            className={cn(className, {
                'take-address-container': variant === CameraVariantLineEnum.POA,
            })}
        >
            {cameraNotAllowed ? (
                <CameraAccessDefault onClose={onClose} />
            ) : (
                <React.Fragment>
                    {currentStep === DocsVerificationInternalStepEnum.CAMERA_ACCESS && (
                        <Spinner className='spinner' />
                    )}
                    {currentStep === DocsVerificationInternalStepEnum.SCANNING && (
                        <React.Fragment>
                            <div className='camera-container'>
                                <div className='camera-header'>
                                    <Typography
                                        tag='div'
                                        variant='h2'
                                        className='title'
                                        // @ts-expect-error __html valid
                                        dangerouslySetInnerHTML={{ __html: title }}
                                    />
                                </div>
                                <Webcam
                                    ref={webcamRef}
                                    audio={false}
                                    videoConstraints={videoConstraints}
                                    screenshotFormat='image/jpeg'
                                    screenshotQuality={1}
                                    minScreenshotHeight={2160}
                                    minScreenshotWidth={4096}
                                    width={1920}
                                    height={1080}
                                    onUserMediaError={handleUserMediaError}
                                />
                                {previewFile?.preview && (
                                    <img
                                        className='camera-preview'
                                        src={previewFile.preview as string}
                                        alt='preview'
                                    />
                                )}
                                <div className='corner-line'>
                                    <CornerLine variant='docs' />
                                </div>
                                <div className='camera-actions'>
                                    {previewFile ? (
                                        <React.Fragment>
                                            <Button
                                                variant='secondary'
                                                className='secondary-button'
                                                onClick={handleRedoClick}
                                                size='xl'
                                            >
                                                {t('common.redoPhoto')}
                                            </Button>
                                            <Button
                                                variant='primary'
                                                onClick={handleFileChange}
                                                size='xl'
                                            >
                                                {t('common.continue')}
                                            </Button>
                                        </React.Fragment>
                                    ) : (
                                        <Button
                                            variant='secondary'
                                            className='secondary-button'
                                            onClick={capture}
                                            size='xl'
                                        >
                                            {t('docVerificationWidget.camera.takePhoto')}
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <Icon
                                icon={<CloseIcon />}
                                $clickable={true}
                                className='close-icon'
                                onClick={handleClose}
                            />
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
        </TakePhotoContainer>
    );
};
